/*
 *** Usage in FO: OK
 *** Usage in PO: OK
 *** Usage in BO: OK
 */

import PropTypes from 'prop-types';
import { useCallback, useEffect, useRef, useState } from 'react';
import Carousel from 'react-elastic-carousel';
import styled from 'styled-components';

import { useBreakpoint } from '@hivebrite/design-system/Theme';

import SliderArrow from './components/SliderArrow';
import Col from '../Col';
import Row from '../Row';

export const ITEMS_PER_PAGE_DEFAULT = {
  s: 1,
  m: 2,
  l: 3,
  xl: 4,
};

export const COL_WIDTH_DEFAULT = {
  s: 6 / 6,
  m: 5 / 12,
  l: 4 / 12,
  xl: 3 / 12,
};

/**
 * @deprecated please use `import { Slider } from '@hivebrite/design-system/Slider';` instead
 */
export const SliderRow = ({
  items,
  loading,
  total,
  ItemComponent,
  renderItemComponent,
  LoadingItemComponent,
  renderLoadingItemComponent,
  onLoadMore,
  onShowNext,
  perPageBreakpoints,
  colWidthBreakpoints,
  itemPadding,
  customArrowStyle,
  overflowSliderContainer,
  ...wrapperProps
}) => {
  const ref = useRef();
  const breakpoint = useBreakpoint();

  const perPage = perPageBreakpoints[breakpoint];
  const colWidth = colWidthBreakpoints[breakpoint];
  const sliderWidthPercent = colWidth * perPage * 100;

  const colsFitGrid =
    sliderWidthPercent === 100 || (!loading && total <= perPage);
  // Ensure Cards drop-shadow are visible if colsFitGrid is TRUE or
  // pad the slider to align with the grids columns if colsFitGrid is FALSE
  const paddingRight = colsFitGrid ? '8px' : `${100 - sliderWidthPercent}%`;

  const partiallyVisibleCards = colsFitGrid
    ? 0
    : Math.ceil((100 - sliderWidthPercent) / (colWidth * 100));

  const [current, setCurrent] = useState(0);

  const handleChange = useCallback(({ index }) => {
    setCurrent(index);
  }, []);

  // Determine if we need to request more items
  useEffect(() => {
    if (onLoadMore && !loading) {
      const requiredItems = current + perPage + perPage + partiallyVisibleCards;

      const currentTotal = items.length;

      if (currentTotal < total && requiredItems >= currentTotal) {
        onLoadMore(requiredItems);
      }
    }

    if (onShowNext && current > 1) {
      onShowNext();
    }
  }, [
    current,
    items.length,
    loading,
    onLoadMore,
    onShowNext,
    partiallyVisibleCards,
    perPage,
    total,
  ]);

  const [forceReload, setForceReload] = useState(false);

  // WORKAROUND: If you have 3-4 items rendering at perPage=2 and you are viewing the
  // last items than change to perPage=4 the scroll position will be maintained and the
  // first items will not be visible. This occured on the Events Index page when hiding
  // the map. Note that if you have more items then perPage (5 in this example) the carousel
  // component will correctly reposition the items.
  // I believe this will be fixed by https://github.com/sag1v/react-elastic-carousel/pull/125 but it's not
  // released yet
  useEffect(() => {
    setForceReload(true);
  }, [perPage]);
  useEffect(() => {
    if (forceReload) {
      setForceReload(false);
    }
  }, [forceReload]);
  // END OF WORKAROUND

  // if the ItemComponent supports it, an onTabKeyDown prop function can be used to slide to
  // the next card deck when we are near the end of the current card deck, but the latest card
  // is unreachable (eg. is partially visible to a gradient effect, and thus should not be focused)
  const onTabKeyDown = (index) => {
    // if we are on the latest fully-visible item of a row (so not the test one)
    if (!((index + 1) % perPage)) {
      // then we move to the next slide
      ref.current.slideNext();
    }
  };

  return (
    <Row {...wrapperProps}>
      {!forceReload && (
        <StyledCarousel
          ref={ref}
          paddingRight={paddingRight}
          colsFitGrid={colsFitGrid}
          colWidth={colWidth}
          itemsToShow={perPage}
          itemsToScroll={perPage}
          showArrows={!!items && total > perPage}
          // Click events are still generated on the card being dragged which defeats the purpose
          // of being able to swipe with the mouse. There might be possible workarounds to the problem
          // but mouse-swipping is not mandatory at the moment
          enableMouseSwipe={false}
          pagination={false}
          renderArrow={(props) => (
            <SliderArrow {...props} loading={loading} {...customArrowStyle} />
          )}
          onChange={handleChange}
          breakpoint={breakpoint}
          overflowSliderContainer={overflowSliderContainer}
        >
          {items &&
            items.map((item, index) => (
              <Col key={item.id || index} pr={itemPadding}>
                {/* Keep the props synced with the ItemComponentWrapper in
                hb-react/frontoffice/components/BlocksRenderer/blocks/Slider */}
                {ItemComponent ? (
                  <ItemComponent
                    {...item}
                    index={index}
                    onTabKeyDown={onTabKeyDown}
                  />
                ) : (
                  renderItemComponent({ ...item, index, onTabKeyDown })
                )}
              </Col>
            ))}
          {Array.from({
            // note: instancing an animated loader for all remaining items is very costly
            // so we clamp the max number of items to one page (since user cannot click on "right")
            length: Math.min(
              total > 0
                ? total - items.length
                : perPage + partiallyVisibleCards,
              perPage + partiallyVisibleCards,
            ),
          }).map((_, index) => (
            <Col key={items.length + index} pr={itemPadding}>
              {LoadingItemComponent ? (
                <LoadingItemComponent index={items.length + index} />
              ) : (
                renderLoadingItemComponent({ index: items.length + index })
              )}
            </Col>
          ))}
        </StyledCarousel>
      )}
    </Row>
  );
};

export default SliderRow;

SliderRow.propTypes = {
  loading: PropTypes.bool,
  items: PropTypes.array,
  total: PropTypes.number,
  perPageBreakpoints: PropTypes.object,
  colWidthBreakpoints: PropTypes.object,
  onLoadMore: PropTypes.func,
  onShowNext: PropTypes.func,
  ItemComponent: PropTypes.elementType,
  renderItemComponent: PropTypes.func,
  LoadingItemComponent: PropTypes.elementType,
  renderLoadingItemComponent: PropTypes.func,
  customArrowStyle: PropTypes.object.isRequired,
  itemPadding: PropTypes.object.isRequired,
  overflowSliderContainer: PropTypes.string,
};

SliderRow.defaultProps = {
  items: [],
  perPageBreakpoints: ITEMS_PER_PAGE_DEFAULT,
  colWidthBreakpoints: COL_WIDTH_DEFAULT,
  customArrowStyle: {},
  itemPadding: { _: 3, m: 0 },
};

const StyledCarousel = styled(Carousel).withConfig({
  shouldForwardProp: (prop) =>
    ![
      'paddingRight',
      'colsFitGrid',
      'colWidth',
      'overflowSliderContainer',
    ].includes(prop),
})`
  // Keep cards aligned with rest of grid
  align-items: flex-start;

  // Allow absolute positioning of next/prev buttons
  position: relative;

  & .rec-carousel {
    // Ensure Cards drop-shadow are visible
    width: ${({ colsFitGrid }) => (colsFitGrid ? 'calc(100% + 8px)' : '100%')};
  }

  & .rec-slider-container {
    // Ensure Cards drop-shadow are visible
    margin: 0 -4px -30px 4px;
    // Ensure Cards drop-shadow are visible
    padding-right: ${({ paddingRight }) => paddingRight};
    overflow: ${({ breakpoint, overflowSliderContainer }) =>
      overflowSliderContainer ?? (breakpoint === 's' ? 'visible' : 'hidden')};
  }

  & .rec-slider {
    // Ensure Cards drop-shadow are visible
    padding-left: 4px;
    width: 100%;
    margin-left: -8px;
  }

  & .rec-swipable {
    flex: 1;
  }

  & .rec-carousel-item {
    width: ${({ colWidth }) => colWidth * 100}%;
  }

  & .rec-item-wrapper {
    // Ensure Cards drop-shadow are visible
    overflow: visible;
    height: 100%;
    max-width: 100%;
  }
`;
